import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../atoms/popover';
import { Button } from '../atoms/button';
import { Input } from '../atoms/input';
import { Label } from '../atoms/label';
import { ChevronDown } from 'lucide-react';

export type ColorPickerProps = {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  className?: string;
};

const PRESET_COLORS = [
  '#000000', '#ffffff', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#6366f1', '#8b5cf6',
  '#ec4899', '#64748b', '#1e293b', '#dc2626', '#ea580c', '#059669', '#0284c7', '#4f46e5',
];

const ColorPicker: React.FC<ColorPickerProps> = ({ label, value = '#ffffff', onChange, className }) => {
  const [inputValue, setInputValue] = useState(value);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange(newValue);
    }
  };

  const handlePresetClick = (color: string) => {
    setInputValue(color);
    onChange(color);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="secondary-gray"
          className={`w-full justify-between ${className || ''}`}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-6 h-6 rounded-sm border border-gray-300"
              style={{ backgroundColor: value }}
            />
            <span className="text-sm font-normal">{label}</span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="hex-input">Hex Color</Label>
            <Input
              id="hex-input"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="#000000"
              className="font-mono"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Preset Colors</Label>
            <div className="grid grid-cols-8 gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className="w-6 h-6 rounded border-2 border-gray-200 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ backgroundColor: color }}
                  onClick={() => handlePresetClick(color)}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="native-picker">Custom Color</Label>
            <input
              id="native-picker"
              type="color"
              value={value}
              onChange={(e) => {
                setInputValue(e.target.value);
                onChange(e.target.value);
              }}
              className="w-full h-10 rounded border border-gray-300 cursor-pointer"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColorPicker;
